import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Lock, ShieldCheck, Sparkles, Trophy } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

/**
 * Frozen wallet. PickemSports takes no entry fees and pays no cash prizes — the balance is
 * historical only, and the sole charge on the platform is a subscription.
 */
export default function Wallet() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);

  usePageMeta({
    title: "Wallet · PickemSports.club",
    description: "Wallet history. PickemSports charges no entry fees and pays no cash prizes.",
    path: "/wallet",
    noindex: true,
  });

  useEffect(() => {
    api.get("/wallet/cashouts").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-2">Account</div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight">WALLET</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="border border-white/10 bg-[#141414] p-6" data-testid="wallet-balance-card">
          <div className="flex items-center justify-between mb-1">
            <div className="text-white/40 text-xs uppercase tracking-widest">Balance</div>
            <span
              className="text-[10px] uppercase tracking-widest text-[#FF9F0A] border border-[#FF9F0A]/40 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1"
              data-testid="wallet-frozen-badge"
            >
              <Lock size={10} /> Frozen
            </span>
          </div>
          <div className="font-display text-5xl text-[#00FF66]" data-testid="wallet-balance">
            ${(user?.wallet_balance || 0).toFixed(2)}
          </div>
          <p className="text-white/50 text-sm mt-3">
            Historical balance only. Deposits and cash payouts are switched off — nothing can be
            added to or taken out of this balance.
          </p>
        </div>

        <div className="lg:col-span-2 border border-[#00FF66]/30 bg-[#00FF66]/[0.04] p-6"
          data-testid="no-money-notice">
          <div className="inline-flex items-center gap-2 text-[#00FF66] text-xs uppercase tracking-widest mb-2">
            <ShieldCheck size={15} /> How PickemSports works now
          </div>
          <h2 className="font-display text-3xl mb-3">FREE TO PLAY. ALWAYS.</h2>
          <ul className="space-y-2 text-white/70 text-sm">
            <li className="flex gap-2">
              <span className="text-[#00FF66]">✓</span>
              <span><strong>No entry fees.</strong> Every challenge is a free skills contest — we never take money to enter.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#00FF66]">✓</span>
              <span><strong>No cash prizes.</strong> You play for bragging rights, XP and the leaderboard.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#00FF66]">✓</span>
              <span><strong>No payouts.</strong> There is no cash-out feature, no bank connection and no wallet funding.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#00FF66]">✓</span>
              <span><strong>One charge only:</strong> an optional subscription for access and AI analytics.</span>
            </li>
          </ul>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/dashboard"
              data-testid="wallet-see-plans-btn"
              className="bg-[#00FF66] hover:bg-[#00e65b] text-black font-bold uppercase tracking-widest px-4 py-2.5 text-xs rounded-sm inline-flex items-center gap-2"
            >
              <Sparkles size={13} /> See subscription plans
            </Link>
            <Link
              to="/lobby"
              data-testid="wallet-play-free-btn"
              className="border border-white/20 hover:border-white/40 text-white font-bold uppercase tracking-widest px-4 py-2.5 text-xs rounded-sm inline-flex items-center gap-2"
            >
              <Trophy size={13} /> Play a free challenge
            </Link>
          </div>
        </div>
      </div>

      {history.length > 0 && (
        <div className="mt-6 border border-white/10 bg-[#141414]" data-testid="legacy-cashouts">
          <div className="p-4 border-b border-white/10">
            <div className="text-white/40 text-xs uppercase tracking-widest">Archive</div>
            <div className="font-display text-xl">EARLIER PAYOUT REQUESTS</div>
            <p className="text-white/40 text-xs mt-1">
              Raised before payouts were retired. Kept for your records — no further action happens here.
            </p>
          </div>
          <div className="divide-y divide-white/10">
            {history.map((c) => (
              <div key={c.cashout_id} className="flex items-center justify-between p-3 text-sm"
                data-testid={`legacy-cashout-${c.cashout_id}`}>
                <div>
                  <div className="font-semibold">${Number(c.amount || 0).toFixed(2)}</div>
                  <div className="text-white/40 text-[10px] uppercase tracking-widest">
                    {new Date(c.created_at).toLocaleDateString()}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-white/50">{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}
